import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError } from "../lib/alerts";
import { asArray, formatCurrency, formatDate, unwrapData } from "../lib/catalog";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";

function FlightLegList({ title, legs }) {
  return (
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <h5 className="mb-3">{title}</h5>
        {legs.length > 0 ? (
          <div className="list-group list-group-flush">
            {legs.map((leg) => (
              <div className="list-group-item px-0" key={leg.id || `${leg.flight_code}-${leg.departure_city}-${leg.arrival_city}`}>
                <div className="fw-semibold">{leg.airlines || "Airline not set"}</div>
                <div className="text-secondary small">
                  {leg.departure_city || "N/A"} to {leg.arrival_city || "N/A"} | {leg.flight_class || "Class not set"}
                </div>
                <div className="small mt-1">
                  {formatDate(leg.departure_date)} {leg.departure_time || ""} → {formatDate(leg.arrival_date)} {leg.arrival_time || ""}
                </div>
                <div className="text-secondary small">Flight: {leg.flight_code || "Not set"} | Stops: {leg.stops ?? "Not set"}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-secondary small">No {title.toLowerCase()} returned by the current API.</div>
        )}
      </div>
    </div>
  );
}

export default function FlightDetailPage() {
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    async function loadDetails() {
      try {
        const payload = await agentApi.get(`/flight/blocks/${encodeURIComponent(id)}/details`);
        if (!alive) return;
        setDetails(unwrapData(payload));
      } catch (fetchError) {
        if (!alive) return;
        showApiError(fetchError, "LOAD_FLIGHT");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadDetails();

    return () => {
      alive = false;
    };
  }, [id]);

  const block = details?.block || {};
  const onwards = asArray(details?.onwards);
  const returns = asArray(details?.returns);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Flight detail"
          title={block.block_name || "Live flight block"}
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Flights", href: "/flights" },
            { label: block.block_name || "Flight Details" },
          ]}
          backTo={true}
        />

        {loading ? <PageLoader message="Loading flight details..." /> : null}

        {!loading && details ? (
          <>
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between gap-3 mb-3">
                  <StatusBadge value={block.status || "Unknown"} />
                  <span className="text-secondary small">{block.block_code || "No code"}</span>
                </div>
                <div className="row g-3 small">
                  <div className="col-6 col-md-3"><strong>Adult</strong><br />{formatCurrency(block.adult_price)}</div>
                  <div className="col-6 col-md-3"><strong>Child</strong><br />{formatCurrency(block.child_price)}</div>
                  <div className="col-6 col-md-3"><strong>Infant</strong><br />{formatCurrency(block.infant_price)}</div>
                  <div className="col-6 col-md-3"><strong>Seats</strong><br />{block.available_seats ?? "Not set"}</div>
                  <div className="col-6 col-md-3"><strong>PNR</strong><br />{block.pnr || "Not set"}</div>
                  <div className="col-6 col-md-3"><strong>Vendor contact</strong><br />{block.vendor_contact || "Not set"}</div>
                  <div className="col-12"><strong>Terms</strong><br />{block.terms_condition || "Not provided"}</div>
                </div>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-12 col-lg-6">
                <FlightLegList title="Onward flights" legs={onwards} />
              </div>
              <div className="col-12 col-lg-6">
                <FlightLegList title="Return flights" legs={returns} />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </AgentLayout>
  );
}
